import { Chip, Skeleton } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ConversationViewer } from "@/src/components/ConversationViewer";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { ConversationSummary } from "@/src/libs/moderation.type";
import { relativeTime } from "@/src/libs/relativeTime";

/**
 * Les discussions d'un compte.
 *
 * ## Une liste, pas trente fils chargés
 *
 * Chaque ligne dit qui, combien et quand ; le fil ne se charge qu'au clic sur
 * « Lire ». Ce n'est pas seulement une économie de requêtes : le serveur
 * **journalise chaque lecture de discussion**, et tout précharger inscrirait au
 * registre trente consultations qui n'ont pas eu lieu.
 *
 * ## Ce que chaque ligne doit dire avant qu'on l'ouvre
 *
 * Avec **qui** — c'est la première question devant un dossier de harcèlement —
 * et depuis quand elle est silencieuse. Un fil d'événement porte en plus le nom
 * de son événement : « conversation de groupe » sans dire laquelle n'apprend
 * rien.
 */
export function AccountConversations({
  conversations,
  isLoading,
}: {
  conversations: ConversationSummary[];
  isLoading: boolean;
}) {
  const t = useTranslations("conversations");
  const { locale } = useLocale();

  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-2">
        {[0, 1, 2].map((row) => (
          <Skeleton className="h-14 w-full" key={row} />
        ))}
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {conversations.map((conversation) => (
        <li
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-surface p-3"
          key={conversation.id}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Chip size="sm" variant="secondary">
                {t(
                  conversation.type === "group" ? "kind-group" : "kind-direct",
                )}
              </Chip>

              {conversation.event ? (
                <span className="truncate text-sm">
                  {t("event-thread", { name: conversation.event.name })}
                </span>
              ) : conversation.title ? (
                <span className="truncate text-sm">{conversation.title}</span>
              ) : null}

              {/* Un départ change ce qu'on lit : la personne n'a plus accès au
                  fil, mais ce qu'elle y a écrit reste. */}
              {conversation.leftAt ? (
                <Chip size="sm" variant="secondary">
                  {t("left")}
                </Chip>
              ) : null}
            </div>

            {/* Avec qui : la première question devant un dossier. */}
            <ul className="flex flex-wrap items-center gap-2">
              {conversation.members.map((member) => (
                <li key={member.id}>
                  <AccountBadge account={member} isLinked />
                </li>
              ))}

              {conversation.members.length <
              conversation.participantCount - 1 ? (
                <li className="text-xs text-muted">
                  {t("more-members", {
                    count:
                      conversation.participantCount -
                      1 -
                      conversation.members.length,
                  })}
                </li>
              ) : null}
            </ul>

            <p className="text-xs text-muted">
              {t("message-count", { count: conversation.messageCount })}
              {conversation.lastMessageAt
                ? ` · ${relativeTime(conversation.lastMessageAt, locale, Date.now())}`
                : ` · ${t("silent")}`}
            </p>
          </div>

          <ConversationViewer
            conversationId={conversation.id}
            label={t("open")}
            title={t("title")}
          />
        </li>
      ))}
    </ul>
  );
}
